import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tagopsService } from '../services/tagopsService';
import { useAuth } from './AuthContext';
import { ResourceInformation, ScanResultDetail } from '../types/tagops';

interface ScanResult {
  service: string;
  resourceType: string;
  region: string;
  resources: ResourceInformation[];
  loading: boolean;
  error?: string;
  scanned_resources?: number;
  resources_updated?: number;
  resources_skipped?: number;
  details?: any[];
}

interface BulkScanResult {
  [resourceType: string]: ScanResultDetail;
}

interface FullAccountScanResult {
  [service: string]: {
    [resourceType: string]: ScanResultDetail;
  };
}

interface ScanContextType {
  // Single scan results
  singleScanResults: ScanResult[];
  singleScanLoading: boolean;
  singleScanError: string | null;
  
  // Bulk scan results
  bulkScanResults: BulkScanResult | null;
  bulkScanLoading: boolean;
  bulkScanError: string | null;
  
  // Full account scan results
  fullAccountScanResults: FullAccountScanResult | null;
  fullAccountScanLoading: boolean;
  fullAccountScanError: string | null;
  
  // Cache management
  lastFetched: {
    singleScan: Date | null;
    bulkScan: Date | null;
    fullAccountScan: Date | null;
  };
  isStale: {
    singleScan: boolean;
    bulkScan: boolean;
    fullAccountScan: boolean;
  };
  
  // Actions
  performSingleScan: (service: string, resourceType: string, region: string, dryRun?: boolean) => Promise<void>;
  performBulkScan: (service: string, resourceTypes: string[], region: string, dryRun?: boolean) => Promise<void>;
  performFullAccountScan: (region: string, dryRun?: boolean) => Promise<void>;
  clearSingleScanResults: () => void;
  clearBulkScanResults: () => void;
  clearFullAccountScanResults: () => void;
  clearAllScanResults: () => void;
}

const ScanContext = createContext<ScanContextType | undefined>(undefined);

// Cache duration: 10 minutes
const SCAN_CACHE_DURATION = 10 * 60 * 1000;

// Cache keys for localStorage
const SINGLE_SCAN_CACHE_KEY = 'tagops_single_scan_cache';
const BULK_SCAN_CACHE_KEY = 'tagops_bulk_scan_cache';
const FULL_ACCOUNT_SCAN_CACHE_KEY = 'tagops_full_account_scan_cache';
const ACTIVITY_CACHE_KEY = 'tagops_last_activity';

interface CachedData<T> {
  data: T;
  timestamp: number;
  params: any; // Store the parameters used for the scan
}

export const useScan = () => {
  const context = useContext(ScanContext);
  if (context === undefined) {
    throw new Error('useScan must be used within a ScanProvider');
  }
  return context;
};

interface ScanProviderProps {
  children: React.ReactNode;
}

export const ScanProvider: React.FC<ScanProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Single scan state
  const [singleScanResults, setSingleScanResults] = useState<ScanResult[]>([]);
  const [singleScanLoading, setSingleScanLoading] = useState(false);
  const [singleScanError, setSingleScanError] = useState<string | null>(null);
  
  // Bulk scan state
  const [bulkScanResults, setBulkScanResults] = useState<BulkScanResult | null>(null);
  const [bulkScanLoading, setBulkScanLoading] = useState(false);
  const [bulkScanError, setBulkScanError] = useState<string | null>(null);
  
  // Full account scan state
  const [fullAccountScanResults, setFullAccountScanResults] = useState<FullAccountScanResult | null>(null);
  const [fullAccountScanLoading, setFullAccountScanLoading] = useState(false);
  const [fullAccountScanError, setFullAccountScanError] = useState<string | null>(null);
  
  // Cache tracking
  const [lastFetched, setLastFetched] = useState({
    singleScan: null as Date | null,
    bulkScan: null as Date | null,
    fullAccountScan: null as Date | null,
  });

  // Check if data is stale
  const isStale = {
    singleScan: lastFetched.singleScan ? 
      (Date.now() - lastFetched.singleScan.getTime()) > SCAN_CACHE_DURATION : true,
    bulkScan: lastFetched.bulkScan ? 
      (Date.now() - lastFetched.bulkScan.getTime()) > SCAN_CACHE_DURATION : true,
    fullAccountScan: lastFetched.fullAccountScan ? 
      (Date.now() - lastFetched.fullAccountScan.getTime()) > SCAN_CACHE_DURATION : true,
  };

  // Load data from cache on mount
  useEffect(() => {
    const loadSingleScanFromCache = () => {
      try {
        const cached = localStorage.getItem(SINGLE_SCAN_CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData<ScanResult[]> = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < SCAN_CACHE_DURATION) {
            setSingleScanResults(data);
            setLastFetched(prev => ({ ...prev, singleScan: new Date(timestamp) }));
          } else {
            localStorage.removeItem(SINGLE_SCAN_CACHE_KEY);
            setSingleScanResults([]);
            setLastFetched(prev => ({ ...prev, singleScan: null }));
          }
        }
      } catch (err) {
        localStorage.removeItem(SINGLE_SCAN_CACHE_KEY);
        setSingleScanResults([]);
        setLastFetched(prev => ({ ...prev, singleScan: null }));
      }
    };

    const loadBulkScanFromCache = () => {
      try {
        const cached = localStorage.getItem(BULK_SCAN_CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData<BulkScanResult> = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < SCAN_CACHE_DURATION) {
            setBulkScanResults(data);
            setLastFetched(prev => ({ ...prev, bulkScan: new Date(timestamp) }));
          } else {
            localStorage.removeItem(BULK_SCAN_CACHE_KEY);
            setBulkScanResults(null);
            setLastFetched(prev => ({ ...prev, bulkScan: null }));
          }
        }
      } catch (err) {
        localStorage.removeItem(BULK_SCAN_CACHE_KEY);
        setBulkScanResults(null);
        setLastFetched(prev => ({ ...prev, bulkScan: null }));
      }
    };

    const loadFullAccountScanFromCache = () => {
      try {
        const cached = localStorage.getItem(FULL_ACCOUNT_SCAN_CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData<FullAccountScanResult> = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < SCAN_CACHE_DURATION) {
            setFullAccountScanResults(data);
            setLastFetched(prev => ({ ...prev, fullAccountScan: new Date(timestamp) }));
          } else {
            localStorage.removeItem(FULL_ACCOUNT_SCAN_CACHE_KEY);
            setFullAccountScanResults(null);
            setLastFetched(prev => ({ ...prev, fullAccountScan: null }));
          }
        }
      } catch (err) {
        localStorage.removeItem(FULL_ACCOUNT_SCAN_CACHE_KEY);
        setFullAccountScanResults(null);
        setLastFetched(prev => ({ ...prev, fullAccountScan: null }));
      }
    };

    loadSingleScanFromCache();
    loadBulkScanFromCache();
    loadFullAccountScanFromCache();
  }, []);

  // Track user activity for smart refresh
  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem(ACTIVITY_CACHE_KEY, Date.now().toString());
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    updateActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Save data to cache
  const saveSingleScanToCache = useCallback((data: ScanResult[], params: any) => {
    try {
      const cacheData: CachedData<ScanResult[]> = {
        data,
        timestamp: Date.now(),
        params
      };
      localStorage.setItem(SINGLE_SCAN_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  const saveBulkScanToCache = useCallback((data: BulkScanResult, params: any) => {
    try {
      const cacheData: CachedData<BulkScanResult> = {
        data,
        timestamp: Date.now(),
        params
      };
      localStorage.setItem(BULK_SCAN_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  const saveFullAccountScanToCache = useCallback((data: FullAccountScanResult, params: any) => {
    try {
      const cacheData: CachedData<FullAccountScanResult> = {
        data,
        timestamp: Date.now(),
        params
      };
      localStorage.setItem(FULL_ACCOUNT_SCAN_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  // Check if user is active
  const isUserActive = useCallback(() => {
    const lastActivity = localStorage.getItem(ACTIVITY_CACHE_KEY);
    const now = Date.now();
    const activityThreshold = 10 * 60 * 1000; // 10 minutes
    const isTabVisible = !document.hidden;
    
    return isTabVisible && (!lastActivity || (now - parseInt(lastActivity)) < activityThreshold);
  }, []);

  // Single scan function
  const performSingleScan = useCallback(async (service: string, resourceType: string, region: string, dryRun: boolean = false) => {
    setSingleScanLoading(true);
    setSingleScanError(null);

    const resultKey = `${service}-${resourceType}-${region}-${dryRun ? 'dry' : 'live'}`;
    const params = { service, resourceType, region, dryRun };

    try {
      // Check if we have authentication tokens
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('access_token');
      
      if (!idToken && !accessToken) {
        throw new Error('No authentication tokens found');
      }

      const resources = await tagopsService.scanResourcesDetailed(service, resourceType, region, dryRun);
      
      const scanResult: ScanResult = {
        service,
        resourceType,
        region,
        resources: resources.details?.map((detail: any) => ({
          arn: detail.arn,
          id: detail.id,
          name: detail.name,
          tags: detail.current_tags || detail.previous_tags || [],
          region,
          account: '',
          serviceResourceType: service,
          type: resourceType
        })) || [],
        loading: false,
        scanned_resources: resources.scanned_resources,
        resources_updated: resources.resources_updated,
        resources_skipped: resources.resources_skipped,
        details: resources.details
      };

      // Update results
      setSingleScanResults(prev => {
        const filtered = prev.filter(r => !(r.service === service && r.resourceType === resourceType && r.region === region));
        const updated = [...filtered, scanResult];
        saveSingleScanToCache(updated, params);
        return updated;
      });

      setLastFetched(prev => ({ ...prev, singleScan: new Date() }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Single scan failed';
      setSingleScanError(errorMessage);
      throw err;
    } finally {
      setSingleScanLoading(false);
    }
  }, [saveSingleScanToCache]);

  // Bulk scan function
  const performBulkScan = useCallback(async (service: string, resourceTypes: string[], region: string, dryRun: boolean = false) => {
    setBulkScanLoading(true);
    setBulkScanError(null);

    const params = { service, resourceTypes, region, dryRun };

    try {
      // Check if we have authentication tokens
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('access_token');
      
      if (!idToken && !accessToken) {
        throw new Error('No authentication tokens found');
      }

      const results = dryRun 
        ? await tagopsService.bulkScanResourcesPreview(service, resourceTypes, region)
        : await tagopsService.bulkScanResources(service, resourceTypes, region);

      setBulkScanResults(results);
      setLastFetched(prev => ({ ...prev, bulkScan: new Date() }));
      saveBulkScanToCache(results, params);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk scan failed';
      setBulkScanError(errorMessage);
      throw err;
    } finally {
      setBulkScanLoading(false);
    }
  }, [saveBulkScanToCache]);

  // Full account scan function
  const performFullAccountScan = useCallback(async (region: string, dryRun: boolean = false) => {
    setFullAccountScanLoading(true);
    setFullAccountScanError(null);

    const params = { region, dryRun };

    try {
      // Check if we have authentication tokens
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('access_token');
      
      if (!idToken && !accessToken) {
        throw new Error('No authentication tokens found');
      }

      const results = dryRun 
        ? await tagopsService.fullAccountScanPreview(region)
        : await tagopsService.fullAccountScan(region);

      setFullAccountScanResults(results);
      setLastFetched(prev => ({ ...prev, fullAccountScan: new Date() }));
      saveFullAccountScanToCache(results, params);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Full account scan failed';
      setFullAccountScanError(errorMessage);
      throw err;
    } finally {
      setFullAccountScanLoading(false);
    }
  }, [saveFullAccountScanToCache]);

  // Clear functions
  const clearSingleScanResults = useCallback(() => {
    setSingleScanResults([]);
    setSingleScanError(null);
    localStorage.removeItem(SINGLE_SCAN_CACHE_KEY);
    setLastFetched(prev => ({ ...prev, singleScan: null }));
  }, []);

  const clearBulkScanResults = useCallback(() => {
    setBulkScanResults(null);
    setBulkScanError(null);
    localStorage.removeItem(BULK_SCAN_CACHE_KEY);
    setLastFetched(prev => ({ ...prev, bulkScan: null }));
  }, []);

  const clearFullAccountScanResults = useCallback(() => {
    setFullAccountScanResults(null);
    setFullAccountScanError(null);
    localStorage.removeItem(FULL_ACCOUNT_SCAN_CACHE_KEY);
    setLastFetched(prev => ({ ...prev, fullAccountScan: null }));
  }, []);

  const clearAllScanResults = useCallback(() => {
    clearSingleScanResults();
    clearBulkScanResults();
    clearFullAccountScanResults();
  }, [clearSingleScanResults, clearBulkScanResults, clearFullAccountScanResults]);

  const value: ScanContextType = {
    singleScanResults,
    singleScanLoading,
    singleScanError,
    bulkScanResults,
    bulkScanLoading,
    bulkScanError,
    fullAccountScanResults,
    fullAccountScanLoading,
    fullAccountScanError,
    lastFetched,
    isStale,
    performSingleScan,
    performBulkScan,
    performFullAccountScan,
    clearSingleScanResults,
    clearBulkScanResults,
    clearFullAccountScanResults,
    clearAllScanResults,
  };

  return (
    <ScanContext.Provider value={value}>
      {children}
    </ScanContext.Provider>
  );
};
